/**
 * Подсказки частых комментариев при вводе траты (SPEC-055, UX пересмотрен SPEC-056).
 *
 * Считается целиком на клиенте: `/v1/bootstrap` уже отдаёт все траты, они лежат
 * в кэше react-query — второй источник тех же данных не нужен, сетевых запросов
 * фича не добавляет.
 *
 * Ранжирование — частота с экспоненциальным затуханием («frecency», как в истории
 * браузеров). Период полураспада на результат не влияет вовсе (30 и 180 дней дали
 * одинаковый hit-rate), поэтому взяты спокойные 60 дней.
 *
 * Считаем ВНУТРИ выбранной категории (SPEC-056): замер на боевой истории показал
 * 63% попадания в топ-5 против 41% у глобального списка. Глобальные подсказки
 * пробовали на главном экране и убрали — там категория ещё не выбрана, и список
 * оказывался шумом («Такси» в продуктах).
 *
 * Отдельный файл, а не utils.ts: здесь своя предметная область (нормализация
 * текста и ранжирование), которую тестируют изолированно.
 */

/** Период полураспада веса траты, дни. */
export const HALF_LIFE_DAYS = 60;

/** Сколько подсказок показываем в экране описания. */
export const NOTE_SCREEN_LIMIT = 8;

export interface NoteSource { note: string | null; date: string; category_id?: string | null }

export interface Suggestion { note: string; weight: number }

/**
 * Ключ сравнения: NFKC + casefold + схлопывание пробелов. Тот же алгоритм, что в
 * Worker'е (`note-cleanup.ts`), и по той же причине считается в JS: lower() в
 * SQLite/D1 кириллицу не трогает.
 */
export function normKey(s: string): string {
    return s.normalize("NFKC").toLowerCase().trim().replace(/\s+/g, " ");
}

function daysBetween(fromISO: string, toISO: string): number {
    const a = Date.parse(fromISO), b = Date.parse(toISO);
    if (Number.isNaN(a) || Number.isNaN(b)) return 0;
    return Math.max(0, (b - a) / 86_400_000);
}

/**
 * Список подсказок по истории трат, от самой вероятной к менее вероятной.
 *
 * `categoryId` сужает историю до одной категории — это и есть контекст, ради
 * которого ввод описания переехал за выбор категории (SPEC-056). Категория без
 * истории даёт пустой список: добивать его глобальными частыми нельзя, иначе в
 * «Продуктах» снова появятся «Доставка» и «Такси».
 *
 * Показываем то написание, которое владелец использовал чаще всего: подсказка —
 * это его собственный текст, а не наша нормализованная форма. Одинаковые веса
 * разводим по алфавиту, чтобы порядок не «дышал» между рендерами.
 */
export function rankSuggestions(rows: NoteSource[], today: string, categoryId?: string | null): Suggestion[] {
    const weight = new Map<string, number>();
    const variants = new Map<string, Map<string, number>>();

    for (const r of rows) {
        if (categoryId !== undefined && r.category_id !== categoryId) continue;
        const raw = (r.note ?? "").trim();
        if (!raw) continue;
        const key = normKey(raw);
        if (!key) continue;
        const w = Math.pow(0.5, daysBetween(r.date, today) / HALF_LIFE_DAYS);
        weight.set(key, (weight.get(key) ?? 0) + w);
        const byVariant = variants.get(key) ?? new Map<string, number>();
        byVariant.set(raw, (byVariant.get(raw) ?? 0) + 1);
        variants.set(key, byVariant);
    }

    const out: Suggestion[] = [];
    for (const [key, w] of weight) {
        const byVariant = variants.get(key)!;
        let best = "", bestCount = -1;
        for (const [text, count] of byVariant) {
            if (count > bestCount || (count === bestCount && text.localeCompare(best) < 0)) {
                best = text; bestCount = count;
            }
        }
        out.push({ note: best, weight: w });
    }
    return out.sort((a, b) => b.weight - a.weight || a.note.localeCompare(b.note));
}

/**
 * Фильтр по уже набранному тексту — по началу значения, не по вхождению.
 * Замер: на первом символе префикс даёт 67% попадания против 60% у подстроки;
 * подстрока выигрывает только с третьего символа, когда список и так короткий.
 */
export function filterSuggestions(all: Suggestion[], query: string, limit: number): Suggestion[] {
    const q = normKey(query);
    const matched = q ? all.filter((s) => normKey(s.note).startsWith(q) && normKey(s.note) !== q) : all;
    return matched.slice(0, limit);
}
